import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, PackageOpen } from "lucide-react";
import { API, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/lib/content";

export default function MyPacks() {
  usePageTitle(
    "My Packs — Sitebound",
    "Access your purchased Sitebound packs — open your Job Seeker Hub or download your files."
  );
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [verified, setVerified] = useState(true);
  const [products, setProducts] = useState({});

  useEffect(() => {
    api
      .get("/products")
      .then((r) => {
        const map = {};
        for (const p of r.data) map[p.slug] = p;
        setProducts(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setOrders(null);
    api
      .get("/orders/mine")
      .then((r) => {
        setVerified(r.data.verified !== false);
        setOrders(r.data.orders || []);
      })
      .catch(() => setOrders([]));
  }, [user]);

  // one card per purchased product (latest order first — orders arrive newest-first)
  const packs = [];
  const seen = new Set();
  for (const o of orders || []) {
    if (seen.has(o.slug)) continue;
    seen.add(o.slug);
    packs.push(o);
  }

  return (
    <div data-testid="my-packs-page" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <div className="mono-label">My Packs</div>
      <h1 className="font-display mt-4 mb-12 text-5xl font-extrabold uppercase tracking-tight text-slate-100">
        Your packs
      </h1>

      {user === null || (user && orders === null) ? (
        <p className="text-sm text-slate-500">Loading your packs…</p>
      ) : user === false ? (
        <div data-testid="my-packs-signin" className="max-w-lg border border-white/10 bg-[#121418] p-10 text-center">
          <PackageOpen className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-4 text-sm text-slate-400">
            Sign in with the email you purchased with to see your packs.
          </p>
          <Link
            data-testid="my-packs-signin-link"
            to="/account"
            className="mt-6 inline-block bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E04800]"
          >
            Sign in
          </Link>
        </div>
      ) : !verified ? (
        <div data-testid="my-packs-unverified" className="max-w-lg border border-white/10 bg-[#121418] p-10 text-center">
          <p className="text-sm text-slate-400">
            Verify your email to unlock your purchases. You can resend the verification email from
            your account page.
          </p>
          <Link
            data-testid="my-packs-account-link"
            to="/account"
            className="mt-6 inline-block border border-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#FF5500] hover:bg-[#FF5500] hover:text-white"
          >
            Go to account
          </Link>
        </div>
      ) : packs.length === 0 ? (
        <div data-testid="my-packs-empty" className="max-w-lg border border-white/10 bg-[#121418] p-10 text-center">
          <PackageOpen className="mx-auto h-10 w-10 text-slate-600" />
          <p className="mt-4 text-sm text-slate-400">No purchases yet.</p>
          <Link
            data-testid="my-packs-empty-cta"
            to="/product/complete-construction-job-seeker-pack"
            className="mt-6 inline-block bg-[#FF5500] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-[#E04800]"
          >
            Get the Complete Pack
          </Link>
        </div>
      ) : (
        <div className="grid max-w-3xl grid-cols-1 gap-6">
          {packs.map((o) => {
            const files = products[o.slug]?.stats?.files;
            return (
              <div
                key={o.slug}
                data-testid={`pack-card-${o.slug}`}
                className="border border-white/10 bg-[#121418] p-6 sm:p-8"
              >
                <div className="font-mono-ind text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Purchased {o.paid_at ? new Date(o.paid_at).toLocaleDateString("en-AU") : ""}
                </div>
                <h2 className="font-display mt-2 text-2xl font-bold uppercase tracking-tight text-slate-100">
                  {o.product_name}
                </h2>
                <p className="mt-1.5 text-xs uppercase tracking-widest text-slate-500">
                  {files ? `${files} resources · ` : ""}Job Seeker Hub + Downloads
                </p>
                <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-stretch">
                  <div className="flex-1">
                    <Link
                      data-testid={`pack-hub-${o.slug}`}
                      to="/hub"
                      className="block bg-[#FF5500] px-6 py-3.5 text-center text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04800]"
                    >
                      Job Seeker Hub
                    </Link>
                    <p className="mt-2 text-center text-[11px] text-slate-500">
                      Open your interactive Job Seeker Hub
                    </p>
                  </div>
                  <div className="flex-1">
                    <a
                      data-testid={`pack-download-${o.slug}`}
                      href={`${API}/downloads/${o.download_token}`}
                      className="flex items-center justify-center gap-2 border border-[#FF5500] px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-[#FF5500] transition-colors hover:bg-[#FF5500] hover:text-white"
                    >
                      <Download className="h-3.5 w-3.5" /> Download ZIP
                    </a>
                    <p className="mt-2 text-center text-[11px] text-slate-500">
                      Download the complete resource pack
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
